import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { getConversations } from '../../actions/messaging';
import { Theme } from '../../config/theme';

const styles = StyleSheet.create({
  inboxContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  inboxHeader: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    padding: '1rem',
  },
  inboxTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  inboxMain: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem',
  },
  conversationItem: {
    width: '100%',
    textAlign: 'left',
    backgroundColor: '#ffffff',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '1rem',
    padding: '1rem',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    ':hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  conversationHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    width: '3rem',
    height: '3rem',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#ffffff',
    flexShrink: 0,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  conversationInfo: {
    marginLeft: '1rem',
    flex: 1,
    minWidth: 0,
  },
  conversationName: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  conversationTimestamp: {
    fontSize: '0.875rem',
    color: '#6c757d',
    marginLeft: '0.5rem',
  },
  conversationLastMessage: {
    color: '#6c757d',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    lineHeight: 1.3,
    marginTop: '0.25rem',
  },
  conversationUnreadLabel: {
    color: Theme.red,
    fontWeight: 700,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem',
    color: '#6c757d',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    color: '#dc3545',
  },
  retryButton: {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#0084ff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    color: '#6c757d',
    textAlign: 'center',
  },
});

class Inbox extends React.Component {
  constructor(props) {
    super(props);

    // Binding methods
    this.fetchConversations = this.fetchConversations.bind(this);
    this.formatDate = this.formatDate.bind(this);
    this.getUserImage = this.getUserImage.bind(this);
    this.truncateMessage = this.truncateMessage.bind(this);
  }

  componentDidMount() {
    this.fetchConversations();
  }

  getUserImage(otherUser) {
    if (otherUser.image_url && otherUser.image_url.length > 10) {
      return otherUser.image_url;
    }

    if (otherUser.profile_photo && otherUser.profile_photo.length > 10) {
      return otherUser.profile_photo;
    }

    return null;
  }

  fetchConversations() {
    const { dispatch, user } = this.props;
    dispatch(getConversations(user.access_token));
  }

  truncateMessage(message, maxLength = 100) {
    if (!message) return 'No messages yet';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  }

  formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    // If less than 24 hours, show time
    if (diff < 86400000) {
      return date.toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    // If less than 7 days, show day of week
    if (diff < 604800000) {
      return date.toLocaleString([], {
        weekday: 'short',
      });
    }

    // Otherwise show date
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
    });
  }

  render() {
    const { onConversationClick, messaging } = this.props;
    const { conversations } = messaging;

    if (conversations.loading) {
      return (
        <div className={css(styles.inboxContainer)}>
          <div className={css(styles.loadingContainer)}>Loading conversations...</div>
        </div>
      );
    }

    if (conversations.error) {
      return (
        <div className={css(styles.inboxContainer)}>
          <div className={css(styles.errorContainer)}>
            <p>Failed to load conversations: {conversations.error}</p>
            <button onClick={this.fetchConversations} className={css(styles.retryButton)}>
              Retry
            </button>
          </div>
        </div>
      );
    }

    const conversationsData = conversations.data || [];

    if (conversationsData.length === 0) {
      return (
        <div className={css(styles.inboxContainer)}>
          <div className={css(styles.emptyContainer)}>
            <p>You don&apos;t have any conversations yet.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={css(styles.inboxContainer)}>
        <main className={css(styles.inboxMain)}>
          {conversationsData.map(conversation => {
            const userImage = this.getUserImage(conversation.otherUser);
            const initials = conversation.otherUser.name
              ? conversation.otherUser.name
                  .split(' ')
                  .map(n => n[0])
                  .join('')
              : conversation.otherUser.email.charAt(0).toUpperCase();

            return (
              <button
                key={conversation.id}
                onClick={() => onConversationClick(conversation.id)}
                className={css(styles.conversationItem)}
              >
                <div className={css(styles.conversationHeader)}>
                  <div className={css(styles.avatar)}>
                    {userImage ? (
                      <img
                        src={userImage}
                        alt={conversation.otherUser.name || conversation.otherUser.email}
                        className={css(styles.avatarImage)}
                      />
                    ) : (
                      initials
                    )}
                  </div>
                  <div className={css(styles.conversationInfo)}>
                    <div>
                      <span className={css(styles.conversationName)}>
                        {conversation.otherUser.name || conversation.otherUser.email}
                      </span>
                      <span className={css(styles.conversationTimestamp)}>
                        {this.formatDate(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <p className={css(styles.conversationLastMessage)}>
                      {this.truncateMessage(
                        conversation.lastMessage ? conversation.lastMessage.content : null,
                      )}
                      {conversation.unreadCount > 0 && (
                        <span className={css(styles.conversationUnreadLabel)}>
                          {' • '}
                          {`${conversation.unreadCount} unread message${
                            conversation.unreadCount > 1 ? 's' : ''
                          }`}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </main>
      </div>
    );
  }
}

Inbox.propTypes = {
  onConversationClick: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  messaging: PropTypes.object,
};

const mapStateToProps = state => ({
  messaging: state.messaging,
  user: state.user,
});

export default connect(mapStateToProps)(Inbox);
export { Inbox as InboxComponent };



// WEBPACK FOOTER //
// ./src/views/MessengerInbox/inbox.js